/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { OutOfServiceType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface OutOfServiceEventSummaryProps {
  id: string;
  type: OutOfServiceType;
  eventNumber: string;
  estimatedEndTime: Date;
  startTime: Date | null;
  isOpportunisticOOS: boolean | null;
  reasonName: string;
  reasonId: string;
  notes: string | null;
}

export class OutOfServiceEventSummaryRecord extends Record<OutOfServiceEventSummaryProps>({
  id: null as any,
  type: null as any,
  eventNumber: null as any,
  estimatedEndTime: null as any,
  startTime: null as any,
  isOpportunisticOOS: null as any,
  reasonName: null as any,
  reasonId: null as any,
  notes: null as any,
}) {
  public static fromJS(json: {
    id: string;
    type: OutOfServiceType;
    eventNumber: string;
    estimatedEndTime: string | number | Date;
    startTime: string | number | Date | null;
    isOpportunisticOOS: boolean | null;
    reasonName: string;
    reasonId: string;
    notes: string | null;
  }): OutOfServiceEventSummaryRecord {
    if (json == null) {
      throw new Error('OutOfServiceEventSummaryRecord.fromJS: parameter cannot be null');
    }
    return new OutOfServiceEventSummaryRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceEventSummaryJson.id',
      ),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', OutOfServiceType.Unscheduled),
        'OutOfServiceEventSummaryJson.type',
      ),
      eventNumber: getFromOrThrow(
        json,
        (j) => j.eventNumber,
        'OutOfServiceEventSummaryJson.eventNumber',
      ),
      estimatedEndTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'estimatedEndTime', '0001-01-01T00:00:00.00Z'),
          'OutOfServiceEventSummaryJson.estimatedEndTime',
        ),
      ),
      startTime: json.startTime != null ? new Date(json.startTime) : null,
      isOpportunisticOOS: json.isOpportunisticOOS,
      reasonName: getFromOrThrow(
        json,
        (j) => j.reasonName,
        'OutOfServiceEventSummaryJson.reasonName',
      ),
      reasonId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'reasonId', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceEventSummaryJson.reasonId',
      ),
      notes: json.notes,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    type: OutOfServiceType;
    eventNumber: string;
    estimatedEndTime: string | number | Date;
    startTime: string | number | Date | null;
    isOpportunisticOOS: boolean | null;
    reasonName: string;
    reasonId: string;
    notes: string | null;
  }): [OutOfServiceEventSummaryRecord, Error[]] {
    if (json == null) {
      throw new Error('OutOfServiceEventSummaryRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new OutOfServiceEventSummaryRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceEventSummaryJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', OutOfServiceType.Unscheduled),
            'OutOfServiceEventSummaryJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      eventNumber: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => j.eventNumber, 'OutOfServiceEventSummaryJson.eventNumber'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimatedEndTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'estimatedEndTime', '0001-01-01T00:00:00.00Z'),
              'OutOfServiceEventSummaryJson.estimatedEndTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      startTime: tryOrDefault(
        () => (json.startTime != null ? new Date(json.startTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isOpportunisticOOS: tryOrDefault(
        () => json.isOpportunisticOOS,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      reasonName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.reasonName, 'OutOfServiceEventSummaryJson.reasonName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      reasonId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'reasonId', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceEventSummaryJson.reasonId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: OutOfServiceEventSummaryProps) {
    super(props);
    if (props == null) {
      throw new Error('OutOfServiceEventSummaryRecord constructor parameter cannot be null');
    }
  }
}

export abstract class OutOfServiceEventSummaryViewModelBase
  implements OutOfServiceEventSummaryProps
{
  protected readonly _props: OutOfServiceEventSummaryProps;
  private _json?: OutOfServiceEventSummaryProps;

  get id(): string {
    return this._props.id;
  }
  get type(): OutOfServiceType {
    return this._props.type;
  }
  get eventNumber(): string {
    return this._props.eventNumber;
  }
  get estimatedEndTime(): Date {
    return this._props.estimatedEndTime;
  }
  get startTime(): Date | null {
    return this._props.startTime;
  }
  get isOpportunisticOOS(): boolean | null {
    return this._props.isOpportunisticOOS;
  }
  get reasonName(): string {
    return this._props.reasonName;
  }
  get reasonId(): string {
    return this._props.reasonId;
  }
  get notes(): string | null {
    return this._props.notes;
  }

  public get isOpportunistic() {
    return this._props.type === OutOfServiceType.Opportunistic;
  }
  public get isRefurbishmentOrRefresh() {
    return this._props.type === OutOfServiceType.RefurbishmentOrRefresh;
  }
  public get isScheduled() {
    return this._props.type === OutOfServiceType.Scheduled;
  }
  public get isUnscheduled() {
    return this._props.type === OutOfServiceType.Unscheduled;
  }

  public isAnyType(...outOfServiceType: OutOfServiceType[]) {
    return outOfServiceType.includes(this._props.type);
  }

  constructor(props: OutOfServiceEventSummaryProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        type: this._props.type,
        eventNumber: this._props.eventNumber,
        estimatedEndTime: this._props.estimatedEndTime,
        startTime: this._props.startTime,
        isOpportunisticOOS: this._props.isOpportunisticOOS,
        reasonName: this._props.reasonName,
        reasonId: this._props.reasonId,
        notes: this._props.notes,
      };
    }
    return this._json;
  }
}
